/**
 * @file Eclairs en rubans : polylignes en zigzag (segments aleatoires deterministes par graine) rendues comme rubans face camera, deux couches
 *       (halo large colore + coeur fin blanc). Geometrie dynamique a capacite fixe (aucune allocation par frame), serre l'aura « Foudre ».
 */
import { BufferAttribute, BufferGeometry, DoubleSide, DynamicDrawUsage, Mesh, MeshBasicMaterial } from 'three';
import type { Color, Scene, Vector3 } from 'three';
import type { ToonKit } from '../toon/toon';

const MAX_POINTS_PER_BOLT = 16;

const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

export class BoltRibbons {
  readonly glow: Mesh;
  readonly core: Mesh;
  private readonly glowPos: Float32Array;
  private readonly corePos: Float32Array;
  private readonly glowAttr: BufferAttribute;
  private readonly coreAttr: BufferAttribute;
  private readonly indexAttr: BufferAttribute;
  private readonly indices: Uint16Array;
  private readonly pts = new Float32Array(MAX_POINTS_PER_BOLT * 3);
  private readonly glowGeo: BufferGeometry;
  private readonly coreGeo: BufferGeometry;
  private verts = 0;
  private idx = 0;
  private readonly capacityVerts: number;

  /** `maxBolts` polylignes de `MAX_POINTS_PER_BOLT` points au plus. */
  constructor(
    private readonly scene: Scene,
    kit: ToonKit,
    maxBolts: number,
  ) {
    this.capacityVerts = maxBolts * MAX_POINTS_PER_BOLT * 2;
    this.glowPos = new Float32Array(this.capacityVerts * 3);
    this.corePos = new Float32Array(this.capacityVerts * 3);
    this.indices = new Uint16Array(maxBolts * (MAX_POINTS_PER_BOLT - 1) * 6);
    this.indexAttr = new BufferAttribute(this.indices, 1);
    this.indexAttr.setUsage(DynamicDrawUsage);
    this.glowAttr = new BufferAttribute(this.glowPos, 3).setUsage(DynamicDrawUsage);
    this.coreAttr = new BufferAttribute(this.corePos, 3).setUsage(DynamicDrawUsage);
    this.glowGeo = kit.track(new BufferGeometry());
    this.glowGeo.setAttribute('position', this.glowAttr);
    this.glowGeo.setIndex(this.indexAttr);
    this.coreGeo = kit.track(new BufferGeometry());
    this.coreGeo.setAttribute('position', this.coreAttr);
    this.coreGeo.setIndex(this.indexAttr);
    const mat = (color: number, opacity: number): MeshBasicMaterial => kit.track(new MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: DoubleSide }));
    this.glow = new Mesh(this.glowGeo, mat(0x7fc8ff, 0.5));
    this.core = new Mesh(this.coreGeo, mat(0xffffff, 0.95));
    for (const m of [this.glow, this.core]) {
      m.frustumCulled = false;
      m.visible = false;
      m.renderOrder = 4;
      scene.add(m);
    }
  }

  begin(): void {
    this.verts = 0;
    this.idx = 0;
  }

  /**
   * Un eclair de A a B en `segs` segments (<= MAX_POINTS_PER_BOLT - 1) ; deviation laterale `jitter` (dans le plan de l'ecran, nulle aux extremites),
   * largeur du halo `width` (le coeur fait ~40 %). `right` / `up` = base de la camera.
   */
  bolt(ax: number, ay: number, az: number, bx: number, by: number, bz: number, seed: number, jitter: number, segs: number, width: number, right: Vector3, up: Vector3): void {
    const n = Math.min(MAX_POINTS_PER_BOLT - 1, Math.max(2, segs));
    if (this.verts + (n + 1) * 2 > this.capacityVerts) return;
    const p = this.pts;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const env = Math.pow(Math.sin(Math.PI * t), 0.7);
      const jx = (hash01(seed * 3.7 + i * 1.7) - 0.5) * 2 * jitter * env;
      const jy = (hash01(seed * 5.3 + i * 2.9) - 0.5) * 2 * jitter * env;
      p[i * 3] = ax + (bx - ax) * t + right.x * jx + up.x * jy;
      p[i * 3 + 1] = ay + (by - ay) * t + right.y * jx + up.y * jy;
      p[i * 3 + 2] = az + (bz - az) * t + right.z * jx + up.z * jy;
    }
    const base = this.verts;
    for (let i = 0; i <= n; i++) {
      const j = i < n ? i : i - 1;
      const dx = p[(j + 1) * 3]! - p[j * 3]!;
      const dy = p[(j + 1) * 3 + 1]! - p[j * 3 + 1]!;
      const dz = p[(j + 1) * 3 + 2]! - p[j * 3 + 2]!;
      // direction du segment dans le plan de l'ecran -> normale du ruban
      const dr = dx * right.x + dy * right.y + dz * right.z;
      const du = dx * up.x + dy * up.y + dz * up.z;
      const len = Math.hypot(dr, du) || 1;
      const nr = -du / len;
      const nu = dr / len;
      const wx = right.x * nr + up.x * nu;
      const wy = right.y * nr + up.y * nu;
      const wz = right.z * nr + up.z * nu;
      // les extremites s'effilent
      const taper = 0.35 + 0.65 * Math.sin((Math.PI * i) / n);
      const hg = width * 0.5 * taper;
      const hc = hg * 0.38;
      const v = (base + i * 2) * 3;
      const px = p[i * 3]!;
      const py = p[i * 3 + 1]!;
      const pz = p[i * 3 + 2]!;
      this.glowPos[v] = px + wx * hg;
      this.glowPos[v + 1] = py + wy * hg;
      this.glowPos[v + 2] = pz + wz * hg;
      this.glowPos[v + 3] = px - wx * hg;
      this.glowPos[v + 4] = py - wy * hg;
      this.glowPos[v + 5] = pz - wz * hg;
      this.corePos[v] = px + wx * hc;
      this.corePos[v + 1] = py + wy * hc;
      this.corePos[v + 2] = pz + wz * hc;
      this.corePos[v + 3] = px - wx * hc;
      this.corePos[v + 4] = py - wy * hc;
      this.corePos[v + 5] = pz - wz * hc;
    }
    for (let i = 0; i < n; i++) {
      const a = base + i * 2;
      const k = this.idx;
      this.indices[k] = a;
      this.indices[k + 1] = a + 1;
      this.indices[k + 2] = a + 2;
      this.indices[k + 3] = a + 1;
      this.indices[k + 4] = a + 3;
      this.indices[k + 5] = a + 2;
      this.idx += 6;
    }
    this.verts += (n + 1) * 2;
  }

  /** Fin de frame : teinte du halo (`glowColor`), opacite globale `fade`, plages de dessin. */
  end(glowColor: Color, fade: number): void {
    const any = this.idx > 0;
    this.glow.visible = any;
    this.core.visible = any;
    if (!any) return;
    (this.glow.material as MeshBasicMaterial).color.copy(glowColor);
    (this.glow.material as MeshBasicMaterial).opacity = 0.5 * fade;
    (this.core.material as MeshBasicMaterial).opacity = 0.95 * fade;
    this.glowGeo.setDrawRange(0, this.idx);
    this.coreGeo.setDrawRange(0, this.idx);
    this.glowAttr.needsUpdate = true;
    this.coreAttr.needsUpdate = true;
    this.indexAttr.needsUpdate = true;
  }

  hide(): void {
    this.glow.visible = false;
    this.core.visible = false;
  }

  dispose(): void {
    this.scene.remove(this.glow, this.core);
  }
}
